import React from 'react';

import { ChevronDownIcon, LinkIcon } from '../../../assets/icons/icons';
import { cx } from '../../../utils/cx';
import { pluralRu } from '../../ui/DatePicker';
import { CatalogThumb } from '../../ui/CatalogThumb/CatalogThumb';
import { tableStyles } from '../../ui/DataTable/DataTable';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import type { CatalogEntry } from '../../../entities/productAnalogs/model/types';
import styles from './ProductAnalogsTable.module.scss';

interface ProductRowProps {
    entry: CatalogEntry;
    selected: boolean;
    nested?: boolean;
    expanded?: boolean;
    onSelect: (entry: CatalogEntry) => void;
    onToggleExpand?: (id: string) => void;
}

const DATE_FORMAT = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});

function formatDateTime(iso: string): string {
    return DATE_FORMAT.format(new Date(iso)).replace(',', '');
}

export function ProductRow({
    entry,
    selected,
    nested = false,
    expanded,
    onSelect,
    onToggleExpand,
}: ProductRowProps) {
    const isAnalog = entry.role === 'analog';
    const hasAnalogs = !isAnalog && entry.analogsCount > 0;
    const HIGHLIGHT_MS = 2000;

    const handleRowClick = () => {
        onSelect(entry);
        if (hasAnalogs) {
            onToggleExpand?.(entry.id);
        }
    };

    function highlightMainEntry(number: string | number) {
        const row = document.querySelector<HTMLTableRowElement>(`[data-entry-number="${number}"]`);
        if (!row) return;

        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        row.classList.add(styles.flash);
        window.setTimeout(() => row.classList.remove(styles.flash), HIGHLIGHT_MS);
    }

    const name = <div className={styles.name}>{entry.name}</div>;

    return (
        <tr
            className={cx(styles.row, nested && styles.analogRow, expanded && styles.expanded, selected && styles.selected)}
            data-entry-number={entry.number}
            aria-selected={selected}
            onClick={handleRowClick}
        >
            {nested ? (
                <td className={styles.branch} aria-hidden="true">
                    <span className={styles.node} />
                </td>
            ) : null}

            {!nested && <td className={styles.id}>{entry.number}</td>}

            {!nested && (
                <td className={styles.expand}>
                    {hasAnalogs && (
                        <button
                            type="button"
                            className={cx(styles.expandBtn, expanded && styles.open)}
                            aria-label={expanded ? 'Скрыть аналоги' : 'Показать аналоги'}
                            aria-expanded={expanded}
                            onClick={(event) => {
                                event.stopPropagation();
                                onToggleExpand?.(entry.id);
                            }}
                        >
                            <ChevronDownIcon />
                        </button>
                    )}
                </td>
            )}

            <td>
                <div className={styles.product}>
                    <CatalogThumb src={entry.imageUrl} />
                    <span>
                        <div className={styles.name}>{entry.name}</div>
                        {nested && <div className={styles.idInline}>ID: {entry.number}</div>}
                    </span>
                </div>
            </td>

            <td>
                <div className={styles.links}>
                    {isAnalog ? (
                        entry.mainEntryNumber ? (
                            <a
                                href={`#entry-${entry.mainEntryNumber}`}
                                className={styles.link}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    highlightMainEntry(entry.mainEntryNumber!);
                                }}
                            >
                                <LinkIcon />
                                <span>Связан с: ID {entry.mainEntryNumber}</span>
                            </a>
                        ) : (
                            <span className={cx(styles.tag, styles.tagMuted)} title="Основной товар не найден">
                                Основной товар не найден
                            </span>
                        )
                    ) : (
                        <>
                            <span className={styles.tag}>Основной товар</span>
                            {hasAnalogs && (
                                <span className={cx(styles.tag, styles.tagMuted)}>+ {entry.analogsCount} {pluralRu(entry.analogsCount, ['аналог', 'аналога', 'аналогов'])}</span>
                            )}
                        </>
                    )}
                </div>
            </td>

            <td>
                <span className={cx(styles.pill, isAnalog ? styles.pillAnalog : styles.pillMain)}>
                    {isAnalog ? 'Аналог' : 'Основной'}
                </span>
            </td>

            <td className={tableStyles.muted}>
                <a
                    href={`/projects/${entry.projectId}`}
                    className={styles.projectLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                >
                    {entry.projectName}
                </a>
            </td>
            <td className={tableStyles.muted}>{formatDateTime(entry.createdAt)}</td>
            <td className={tableStyles.muted}>{entry.authorName}</td>

        </tr >
    );
}